import React, { ReactElement, useMemo } from "react"
import { Box, Stack, useTheme } from "@carrotfertility/carotene-core"
import { useWatch } from "@carrotfertility/carotene-core-x"
import { useFlags } from "launchdarkly-react-client-sdk"
import { Employee } from "types/Employee"
import { Journeys } from "types/Journeys"
import { MemberJourneyData } from "types/MemberJourney"
import { CarrotPlanFormOption, MenuItemOptionFields, TopicOptionFields } from "./hooks/useCarrotPlanMappings"
import { FieldName } from "./CarrotPlanFormTypes"
import { useLastKnownOptions } from "./hooks/useLastKnownOptions"
import { useDynamicCarrotPlanMappings } from "./hooks/useDynamicCarrotPlanMappings"
import { useResetFormStepsOnJourneyChange } from "./hooks/useResetFormStepsOnJourneyChange"
import { JourneySelectSection, NONE_VALUE } from "./JourneySelection"
import JourneyChangeConfirmation from "./JourneyChangeConfirmation"
import { CarrotPlanSteps } from "./CarrotPlanSteps"
import { FormNavigation } from "./CarrotPlanFormNavigation"
import { AssistantRecommendationsPanel } from "./AdditionalRecommendations"
import { getOnlyPrioritizedMenuItems, getOnlyRequiredMenuItems } from "./utils/autocompleteTransformations"

export type CarrotPlanFormContentWithDynamicOptionsProps = {
  employee: Employee
  benefitEnabledJourneyData: any
  inReview: boolean
  onBack: () => void
  onCancel: () => void
  initialMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  initialTopics: CarrotPlanFormOption<TopicOptionFields>[]
  showJourneyChangeConfirmation: boolean
  memberJourney: MemberJourneyData
  handleJourneyChangeConfirm: () => void
  handleJourneyChangeCancel: () => void
}

export function CarrotPlanFormContentWithDynamicOptions({
  employee,
  benefitEnabledJourneyData,
  inReview,
  onBack,
  onCancel,
  initialMenuItems,
  initialTopics,
  showJourneyChangeConfirmation,
  memberJourney,
  handleJourneyChangeConfirm,
  handleJourneyChangeCancel
}: CarrotPlanFormContentWithDynamicOptionsProps): ReactElement {
  const theme = useTheme()
  const employeeName = employee.preferredName ?? employee.firstName

  const selectedPrimaryJourney = useWatch({ name: FieldName.primaryJourney })
  const selectedPrimaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })

  // Guard against the transient render where primaryJourney has updated but primaryJourneyPhase
  // hasn't been reset yet (useEffect resets it after render). Without this, useCarrotPlanOptions
  // fires with a mismatched journey/stage pair (e.g., PREGNANT + an ART stage ID).
  const isPhaseValidForJourney =
    selectedPrimaryJourneyPhase === NONE_VALUE ||
    ((benefitEnabledJourneyData as Journeys)
      .find((j) => j.journeyId === selectedPrimaryJourney)
      ?.stages.some((s) => s.journeyStageId === selectedPrimaryJourneyPhase) ??
      false)
  const effectiveStage = isPhaseValidForJourney ? selectedPrimaryJourneyPhase : NONE_VALUE

  // Reactive gate: based on the form's *currently selected* primary journey, not the
  // member's recorded journey. Lets the gate flip on/off as the MS edits the dropdown.
  // The LD flag returns the list of journey enum names the recs path is enabled for —
  // empty list (or unset) means dark for everyone.
  const { carrotPlanRecommendationsOverride } = useFlags() as {
    carrotPlanRecommendationsOverride?: { journeys?: string[] }
  }
  const enabledJourneys = carrotPlanRecommendationsOverride?.journeys ?? []
  const selectedJourneyEnum = (benefitEnabledJourneyData as Journeys).find(
    (j) => j.journeyId === selectedPrimaryJourney
  )?.journeyEnumName
  const enableRecommendations = selectedJourneyEnum != null && enabledJourneys.includes(selectedJourneyEnum)

  const { menuItems, topics, recommendedSteps, recommendationsErrorStatus, isLoading } = useDynamicCarrotPlanMappings({
    employee,
    initialMenuItems,
    initialTopics,
    journey: selectedPrimaryJourney,
    stage: effectiveStage,
    enableRecommendations
  })

  const { displayMenuItems, displayTopics } = useLastKnownOptions(menuItems, topics, isLoading)

  const bothJourneyAndStageSelected =
    selectedPrimaryJourney !== NONE_VALUE && selectedPrimaryJourneyPhase !== NONE_VALUE

  const newRequiredItems = useMemo(
    () => (bothJourneyAndStageSelected && !isLoading ? getOnlyRequiredMenuItems(menuItems) : null),
    [bothJourneyAndStageSelected, isLoading, menuItems]
  )

  const newPrioritizedItems = useMemo(
    () => (bothJourneyAndStageSelected && !isLoading ? getOnlyPrioritizedMenuItems(menuItems) : null),
    [bothJourneyAndStageSelected, isLoading, menuItems]
  )

  useResetFormStepsOnJourneyChange({
    journey: selectedPrimaryJourney,
    stage: effectiveStage,
    newRequiredItems,
    newPrioritizedItems,
    enableRecommendations,
    newRecommendedSteps: recommendedSteps
  })

  return (
    <Stack spacing={theme.tokens.spacing.xl}>
      <JourneySelectSection
        journeysWithStages={benefitEnabledJourneyData}
        employeeName={employeeName}
        inReview={inReview}
      />
      {showJourneyChangeConfirmation && (
        <JourneyChangeConfirmation
          memberJourney={memberJourney}
          benefitEnabledJourneyData={benefitEnabledJourneyData}
          onConfirm={handleJourneyChangeConfirm}
          onCancel={handleJourneyChangeCancel}
        />
      )}
      <Stack direction={"row"} spacing={theme.tokens.spacing.md} width={"100%"}>
        <Box
          flexDirection="column"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          display="flex"
          width="600px"
        >
          <CarrotPlanSteps
            employeeName={employeeName}
            inReview={inReview}
            stepOptions={displayMenuItems}
            topicOptions={displayTopics}
            enableRecommendations={enableRecommendations}
            recommendedSteps={recommendedSteps}
            recommendationsErrorStatus={recommendationsErrorStatus}
          />
          <Box flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)} display="flex">
            <FormNavigation onBack={onBack} onCancel={onCancel} inReview={inReview} />
          </Box>
        </Box>
        {!inReview && (
          <AssistantRecommendationsPanel
            benefitEnabledJourneyData={benefitEnabledJourneyData}
            menuItemOptions={menuItems}
            topicOptions={topics}
            isFetchingOptions={isLoading}
          />
        )}
      </Stack>
    </Stack>
  )
}
